// WWI command bunker: a low reinforced-concrete headquarters block with a
// sandbag skirt, an oversized banner-red command pennant on a roof mast (the
// icon read from above) and a map table in the open yard corner. 2x2 tiles.
import { defineModel, standVisit, styleV3, type Part } from "../../lib";

const { box, cyl, slab, poles } = styleV3;

const CONCRETE = "#9a9891";
const CONCRETE_DK = "#807e77";
const KHAKI = "#7a6f4e";
const MUD = "#5d4a33";
const GUNMETAL = "#4a4d52";
const TIMBER = "#8b6438";
const CANVAS = "#cbbd93";
const RED = "#b03a3a";     // banner red — the special/command code
const DARK = "#2b2b28";

const BASE = 0.12;                        // foundation slab top
// The bunker block fills the west/back of the plot; the (+x,+z) yard is open.
const BX = -0.25, BZ = -0.2, BW = 1.25, BD = 1.15;
const WALL_H = 0.52;
const FRONT = BZ + BD / 2;                // the block's +z face
const DOOR_X = BX + 0.25;                 // the entrance, kept clear of sandbags

function body(h: number): Part {
  return box(BW, h, BD, CONCRETE, [BX, BASE + h / 2, BZ]);
}

// The thick overhanging concrete roof slab.
function roofSlab(): Part {
  return box(BW + 0.14, 0.12, BD + 0.14, CONCRETE_DK, [BX, BASE + WALL_H + 0.06, BZ]);
}

function doorway(): Part[] {
  return [
    box(0.3, 0.36, 0.05, DARK, [DOOR_X, BASE + 0.18, FRONT + 0.02]),
    box(0.44, 0.08, 0.1, CONCRETE_DK, [DOOR_X, BASE + 0.4, FRONT + 0.04]), // lintel
  ];
}

// The sandbag skirt hugging the front and east faces, one or two courses.
function sandbags(stacked: boolean): Part[] {
  const out: Part[] = [];
  const bag = (x: number, y: number, z: number, ry: number, c: string): Part =>
    box(0.18, 0.09, 0.12, c, [x, BASE + y, z], [0, ry, 0]);
  for (let i = 0; i < 7; i++) {                     // along the +z face
    const x = BX - BW / 2 + 0.08 + i * 0.19;
    if (Math.abs(x - DOOR_X) < 0.28) continue;      // keep the entrance clear
    out.push(bag(x, 0.045, FRONT + 0.11, (i % 2) * 0.24 - 0.12, i % 2 ? KHAKI : MUD));
    if (stacked && i % 2 === 0) out.push(bag(x + 0.09, 0.13, FRONT + 0.11, 0.1, KHAKI));
  }
  for (let i = 0; i < 5; i++) {                     // along the +x face
    const z = BZ - BD / 2 + 0.12 + i * 0.2;
    out.push(bag(BX + BW / 2 + 0.11, 0.045, z, Math.PI / 2 + (i % 2) * 0.22 - 0.11, i % 2 ? MUD : KHAKI));
    if (stacked && i % 2 === 1) out.push(bag(BX + BW / 2 + 0.11, 0.13, z - 0.05, Math.PI / 2 - 0.08, MUD));
  }
  return out;
}

// The oversized banner-red command pennant on a roof mast — the icon.
function pennant(): Part[] {
  const mx = BX - 0.42, mz = BZ - 0.35;
  return [
    cyl(0.022, 0.022, 0.85, 5, GUNMETAL, [mx, 1.08, mz]),
    box(0.46, 0.28, 0.03, RED, [mx + 0.25, 1.34, mz]),
  ];
}

// Roof furniture: a squat vent and a periscope tube.
function roofGear(): Part[] {
  const top = BASE + WALL_H + 0.12;
  return [
    cyl(0.05, 0.05, 0.14, 6, GUNMETAL, [0.05, top + 0.07, -0.5]),
    cyl(0.02, 0.02, 0.3, 4, GUNMETAL, [0.2, top + 0.15, -0.25]),
    box(0.09, 0.05, 0.05, GUNMETAL, [0.2, top + 0.32, -0.25]),
  ];
}

// The map table in the open yard corner where officers gather.
function mapTable(): Part[] {
  return [
    box(0.06, 0.28, 0.06, TIMBER, [0.5, BASE + 0.14, 0.6]),
    box(0.06, 0.28, 0.06, TIMBER, [0.8, BASE + 0.14, 0.6]),
    box(0.44, 0.05, 0.32, TIMBER, [0.65, BASE + 0.305, 0.6]),
    box(0.3, 0.02, 0.2, CANVAS, [0.63, BASE + 0.34, 0.58], [0, 0.2, 0]), // the map
  ];
}

export const commandBunker = defineModel({
  id: "command-bunker",
  tiles: { w: 2, h: 2 },
  batchId: "wwi",
  name: "Command Bunker",
  tags: ["building", "wwi", "military", "bunker", "command", "headquarters"],
  summary: "A low concrete command bunker flying an oversized red pennant.",
  description:
    "Reinforced-concrete headquarters bunker with a sandbag skirt, a banner-red " +
    "command pennant on a roof mast and a map table in the open yard corner. " +
    "Orders are issued at the entrance.",
  author: "Fable 5",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [slab(1.94, 1.94)] },
    { id: "lower", label: "Lower", parts: () => [
      slab(1.94, 1.94), body(0.24), ...sandbags(false).slice(0, 5), ...poles(1.7, 1.7, 0.7),
    ] },
    { id: "upper", label: "Upper", parts: () => [
      slab(1.94, 1.94), body(WALL_H), ...doorway(), ...sandbags(false), ...poles(1.7, 1.7, 1.1),
    ] },
    { id: "complete", label: "Complete", parts: () => [
      slab(1.94, 1.94), body(WALL_H), roofSlab(), ...doorway(), ...sandbags(true),
      ...pennant(), ...roofGear(), ...mapTable(),
    ] },
  ],
  animations: [],
  // Where a runner or officer stands to receive orders: on open ground in front
  // of the entrance, turned to face the doorway.
  stations: [
    { id: "brief", position: [DOOR_X, BASE, FRONT + 0.4], facing: Math.PI, description: "Where a runner stands to receive orders at the entrance." },
  ],
  interactions: [
    { id: "receive-orders", label: "Receive orders", station: "brief", variants: ["complete"], ...standVisit() },
  ],
});
